import { Fragment, useState } from "react"
import type { FC } from "react"
import { ChevronRightIcon } from "components/ui/customWebviewer/JumpingNavigationBar/icons/ChevronRightIcon"
import styles from "./QuotationContextMenu.module.scss"

export type TContextMenuItem = {
	key: string
	label: string
	/** Right-aligned shortcut hint, e.g. "⌘C". Ignored when `children` is set — a submenu row shows a chevron instead. */
	shortcut?: string
	disabled?: boolean
	/** Render a divider above this item (groups clipboard / elevation / destructive actions). */
	separatorBefore?: boolean
	onSelect?: () => void
	/** Flat submenu revealed on hover, e.g. "Navigate to elevation" listing the connected elevations. */
	children?: TContextMenuItem[]
}

type Props = {
	items: TContextMenuItem[]
}

/**
 * Dark right-click context menu for the quotation viewer (Figma "Menubar" 16416:472978).
 * Presentational only — positioning + dismissal are owned by the host (useQuotationViewer),
 * which renders this into the shadow-DOM overlay at the cursor. REQ-368 uses it for the
 * single "Copy" item; REQ-367 extended it with Duplicate / Connect / Delete and separators;
 * REQ-374 adds the one-level submenu ("Navigate to elevation" for an ecall).
 */
export const QuotationContextMenu: FC<Props> = ({ items }) => {
	const [openKey, setOpenKey] = useState<string | null>(null)

	return (
		<div className={styles.menu} role="menu">
			{items.map((item) => {
				const hasChildren = !!item.children && item.children.length > 0
				const isOpen = hasChildren && openKey === item.key
				return (
					<Fragment key={item.key}>
						{item.separatorBefore ? <div className={styles.separator} role="separator" /> : null}
						{/* No mouseleave here on purpose — the submenu is a sibling of the trigger inside this
						    same wrapper, so moving onto it must not close it. Hovering a DIFFERENT item (with
						    or without its own children) is what switches/closes it instead. */}
						<div className={styles.itemWrapper} onMouseEnter={() => setOpenKey(hasChildren ? item.key : null)}>
							<button
								type="button"
								role="menuitem"
								aria-haspopup={hasChildren ? "menu" : undefined}
								aria-expanded={hasChildren ? isOpen : undefined}
								className={styles.item}
								disabled={item.disabled}
								onClick={hasChildren ? undefined : item.onSelect}
							>
								<span className={styles.label}>{item.label}</span>
								{hasChildren ? (
									<span className={styles.chevron}>
										<ChevronRightIcon />
									</span>
								) : item.shortcut ? (
									<span className={styles.shortcut}>{item.shortcut}</span>
								) : null}
							</button>
							{isOpen ? (
								<div className={styles.submenu} role="menu">
									{item.children?.map((child) => (
										<button
											key={child.key}
											type="button"
											role="menuitem"
											className={styles.item}
											disabled={child.disabled}
											onClick={child.onSelect}
										>
											<span className={styles.label}>{child.label}</span>
										</button>
									))}
								</div>
							) : null}
						</div>
					</Fragment>
				)
			})}
		</div>
	)
}
